import { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";

import {
  MobileHeading,
  NavTabsContentContainer,
  TableAndFiltersContainer,
} from "features/layout/components";

import { ColumnsVisibilityControl } from "common/components/DataTable";
import useColumnVisibility from "common/components/DataTable/useColumnVisibility";
import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

import { ACCOUNTS_COLUMNS, ACCOUNTS_TAB_ITEMS } from "../constants";
import useAccountsFilters from "../hooks/useAccountsFilters";
import AccountSettings from "./AccountSettings";
import AddAccount from "./AddAccount";
import DesktopFilters from "./DesktopLayout/DesktopFilters";
import DesktopTable from "./DesktopLayout/DesktopTable";
import ManageAccounts from "./MobileLayout/ManageAccounts";
import MobileFilters from "./MobileLayout/MobileFilters";
import MobileList from "./MobileLayout/MobileList";
import PendingRentedAccounts from "./PendingRentedAccounts";
import RentAccountsButton from "./RentAccountsButton";

import AccountsPlaceholder from "assets/images/empty-placeholders/accounts.png";
import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

export default function LinkedInAccounts() {
  const isDesktop = useTwBreakpoint("lg");
  const [selectedAccounts, setSelectedAccounts] = useState<WorkspaceAccount[]>(
    [],
  );

  const { accounts, accounts_count: accountsCount } =
    useSelectedWorkspaceContext();

  const {
    filteredAccounts,
    hasSearchParams,
    searchParams,
    setSearchParams,
    searchText,
    setSearchText,
    clearSearchParams,
  } = useAccountsFilters(accounts);

  const { columnVisibility, setColumnVisibility } = useColumnVisibility(
    "accounts",
    ACCOUNTS_COLUMNS,
  );

  useEffect(() => {
    setSelectedAccounts((prev) =>
      prev.filter(({ id }) =>
        filteredAccounts.some((filteredAccount) => filteredAccount.id === id),
      ),
    );
  }, [filteredAccounts]);

  const searchInputComponent = (
    <SearchInput
      variant={isDesktop ? "sm" : "lg"}
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      onClear={() => setSearchText("")}
      placeholder="Search accounts"
      className="lg:w-72"
    />
  );
  const pendingAccountsBanner = (
    <RenderIf condition={!getWhitelabelCustomization()}>
      <PendingRentedAccounts />
    </RenderIf>
  );

  let mainContent: JSX.Element;

  if (!accountsCount) {
    mainContent = (
      <EmptyPlaceholder
        imageSrc={AccountsPlaceholder}
        title="No accounts added yet"
        subtitle="Connect your LinkedIn account to create campaigns, manage, and chat with your leads"
        actionComponent={
          <div className="grid gap-2.5 [&:has(>:only-child)]:grid-cols-1 [&:has(>:nth-child(2))]:grid-cols-2">
            <RenderIf condition={isDesktop}>
              <RentAccountsButton size="md" />
            </RenderIf>
            <AddAccount size="md" />
          </div>
        }
      />
    );
  } else {
    const commonProps = {
      accounts: filteredAccounts,
      selectedAccounts,
      setSelectedAccounts,
    };

    let content = (
      <EmptyPlaceholder
        imageSrc={NoResultsImage}
        title="No accounts found"
        subtitle="There were no results that match your criteria"
        actionComponent={
          <Button
            onClick={() => {
              setSearchText("");
              clearSearchParams();
            }}
          >
            Clear
          </Button>
        }
      />
    );
    if (filteredAccounts.length) {
      content = isDesktop ? (
        <DesktopTable
          {...commonProps}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
        />
      ) : (
        <MobileList {...commonProps} />
      );
    }

    mainContent = isDesktop ? (
      <>
        {pendingAccountsBanner}
        <TableAndFiltersContainer
          searchComponent={searchInputComponent}
          filtersComponent={
            <DesktopFilters
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              clearAllFilters={hasSearchParams && clearSearchParams}
            />
          }
          columnSelectionComponent={
            <ColumnsVisibilityControl
              columns={ACCOUNTS_COLUMNS}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
            />
          }
        >
          {content}
        </TableAndFiltersContainer>
      </>
    ) : (
      <>
        <MobileHeading
          extraComponent={pendingAccountsBanner}
          actionComponent={
            <>
              <AddAccount size="md" />
              {!!selectedAccounts.length && (
                <RestrictedComponent visibleFor={["accounts_full"]}>
                  <ManageAccounts selectedAccounts={selectedAccounts} />
                </RestrictedComponent>
              )}
            </>
          }
          searchComponent={
            <>
              {searchInputComponent}
              <MobileFilters
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                clearSearchParams={hasSearchParams && clearSearchParams}
              />
            </>
          }
        />
        {content}
      </>
    );
  }

  return (
    <div className="flex flex-1 flex-col lg:flex-row gap-x-2 h-full min-h-0">
      <NavTabsContentContainer
        desktopActionComponent={
          <>
            <RentAccountsButton size="sm" />
            <AddAccount size="sm" />
          </>
        }
        tabItems={ACCOUNTS_TAB_ITEMS}
      >
        {mainContent}
      </NavTabsContentContainer>
      <Routes>
        <Route path="/:accountId" element={<AccountSettings />} />
      </Routes>
    </div>
  );
}
